import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  ArticleQuery,
  Breadcrumb,
  BreadcrumbsResolver,
  Category,
  CategoryQuery,
  CategoryService,
  HierarchicalCategories,
  ProductQuery,
  UrlHelperService,
} from '@lobos/library';
import { TranslocoService } from '@ngneat/transloco';
import { Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { WeylandSteelArticle } from '../../../../interfaces/steel-article.model';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbResolver extends BreadcrumbsResolver {
  constructor(
    private translocoService: TranslocoService,
    private categoryQuery: CategoryQuery,
    private categoryService: CategoryService,
    private productQuery: ProductQuery,
    private urlHelper: UrlHelperService,
    private articleQuery: ArticleQuery<WeylandSteelArticle>,
  ) {
    super();
  }

  public override resolve(route: ActivatedRouteSnapshot): Observable<Breadcrumb[]> {
    if (route.routeConfig?.path === 'product/:id' && route.params['id']) {
      return this.resolveProduct(route.params['id']);
    }

    if (route.routeConfig?.path === 'category-filter/:id' && route.params['id']) {
      return this.resolveFilterCategory(route.params['id']);
    }

    if (route.routeConfig?.path === 'article/:id' && route.params['id']) {
      return this.resolveArticle(route.params['id'], route.queryParams['_productId']);
    }

    return of([]);
  }

  private resolveProduct(id: string): Observable<Breadcrumb[]> {
    return this.productQuery.selectById(id).pipe(
      filter((product) => !!product),
      map((product) => this.buildBreadcrumbFromCategoryHierarchy(product.listHierarchicalCategories)),
    );
  }

  private resolveFilterCategory(id: string): Observable<Breadcrumb[]> {
    return this.categoryQuery.selectById(id).pipe(
      filter((category) => !!category),
      map((category: Category) => this.buildBreadcrumbFromCategoryHierarchy(category.listHierarchicalCategories)),
    );
  }

  private resolveArticle(id: string, productId: string): Observable<Breadcrumb[]> {
    return this.articleQuery.selectEntity(id).pipe(
      filter((article) => !!article),
      map((article: WeylandSteelArticle) => {
        const productInfo = article.oProductInfo.find((x) => x.Id === productId || x.shtP48MainProduct === 1);
        const breadcrumbs: Breadcrumb[] = this.buildBreadcrumbFromCategoryHierarchy(
          productInfo ? productInfo.listHierarchicalCategories : article.oProductInfo[0].listHierarchicalCategories,
        );
        breadcrumbs.push({ label: article.sArticleID as string, url: article.sUrlPath[0], translate: false });
        return breadcrumbs;
      }),
    );
  }

  private buildBreadcrumbFromCategoryHierarchy(hierarchy: HierarchicalCategories[]): Breadcrumb[] {
    const breadcrumbs: Breadcrumb[] = [];
    let path = `/${this.translocoService.getActiveLang()}/`;

    hierarchy.forEach((item, index) => {
      if (index > 0) {
        path += `${this.urlHelper.normalize(item.sValue)}/`;
        breadcrumbs.push({ label: item.sValue, url: path, translate: false });
      }
    });

    return breadcrumbs;
  }
}
